import { sentryCaptureFileError } from '@/utils/sentry/sentry-file-error';

export function useFileDownload() {
  const getFileNameFromUrl = (url: string): string => {
    try {
      const parsedUrl = new URL(url);
      const pathname = parsedUrl.pathname;
      const lastSegment = pathname.substring(pathname.lastIndexOf('/') + 1);
      return decodeURIComponent(lastSegment || 'download');
    } catch {
      const lastSlashIndex = url.lastIndexOf('/');
      return decodeURIComponent(
        lastSlashIndex >= 0 ? url.substring(lastSlashIndex + 1) : 'download',
      );
    }
  };

  const getFileNameFromContentDisposition = (contentDisposition: string | null): string | null => {
    if (!contentDisposition) return null;
    const filenameStarMatch = contentDisposition.match(/filename\*\s*=\s*([^']*)'[^']*'([^;]+)$/i);
    if (filenameStarMatch && filenameStarMatch[2]) {
      try {
        return decodeURIComponent(filenameStarMatch[2].trim().replace(/["']/g, ''));
      } catch {
        return filenameStarMatch[2].trim().replace(/["']/g, '');
      }
    }
    const filenameMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
    if (filenameMatch && filenameMatch[1]) {
      return filenameMatch[1].trim().replace(/["']/g, '');
    }
    return null;
  };

  const downloadFile = async (url: string, fallbackName?: string) => {
    let failedStatus: number | undefined;
    try {
      const response = await fetch(url, { method: 'GET' });

      if (!response.ok) {
        failedStatus = response.status;
        throw new Error(`Failed to fetch: ${response.status}`);
      }
      const headerName = getFileNameFromContentDisposition(
        response.headers.get('content-disposition'),
      );
      const fileName = headerName || fallbackName || getFileNameFromUrl(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      sentryCaptureFileError({
        operation: 'download',
        error: err instanceof Error ? err : new Error(String(err)),
        url,
        status: failedStatus,
        filename: fallbackName,
      });
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return { downloadFile };
}
